import React, { createRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, IconButton, Tooltip, Box, Popover, Grid, Chip, LinearProgress, FormControl, FormControlLabel, Checkbox, Select, MenuItem, InputLabel, Typography } from "@material-ui/core";
import { Link as LinkIcon, ImageOutlined as ImageIcon, EmojiEmotionsOutlined as EmojiIcon } from "@material-ui/icons";
import { withSnackbar, ProviderContext } from "notistack";
import EmojiPicker from "emoji-picker-react";
import config from "config";
import { GridTextField } from "components";
import { postStories, postStoriesAudienceSize } from "api/admin/stories";
import { UserProfile } from "api/user";
import uI18n from "utils/i18n";
import { Booking } from "api/bookings";
import { getSportForms, SportForm } from "api/courts";
import { Autocomplete } from "@material-ui/lab";
import i18n from "./i18n";
import "./bookingStyle.scss";
// prop types
interface CreateStoryDialogProps extends ProviderContext {
    bookings?: Booking[],
    show: boolean,
    userProfile: UserProfile | null,
    onClose: () => void,
}

// state types
interface CreateStoryDialogState {
    allowComments: boolean,
    anyOfSportForms: string[],
    attachedMedia: File[],
    content: string,
    generalSportForms: SportForm[],
    insertLinkInvalidUrl: boolean,
    insertLinkText: string,
    insertLinkUrl: string,
    isLoading: boolean,
    postAs: string,
    postIn: string,
    showEmojiPicker: HTMLButtonElement | null,
    showLinkPopover: HTMLButtonElement | null,
    isCompany: boolean | undefined,
    clubSite: string | undefined,
    audienceSize: number,
    showSiteConfirmation: boolean,
}

// initial state
const initialState = {
    allowComments: true,
    anyOfSportForms: [],
    attachedMedia: [],
    content: "",
    generalSportForms: [],
    insertLinkInvalidUrl: false,
    insertLinkText: "",
    insertLinkUrl: "",
    isLoading: false,
    postAs: "user",
    postIn: "",
    showEmojiPicker: null,
    showLinkPopover: null,
    isCompany: undefined,
    clubSite: undefined,
    audienceSize: 0,
    showSiteConfirmation: false,
};

// create story dialog
class CreateStoryDialog extends React.Component<CreateStoryDialogProps, CreateStoryDialogState> {
    private insertMediaRef = createRef<HTMLInputElement>();

    constructor(props: CreateStoryDialogProps) {
        super(props);
        this.state = initialState;
    }

    // did mount -> fetch general sport forms
    componentDidMount = async () => {
        this.setState({ generalSportForms: await getSportForms() });
        await this.getAudienceSize();
    };

    // handle insert emoji
    onEmojiClick = (emoji: string) => {
        this.setState(({ content }) => ({
            content: `${content}${emoji}`,
            showEmojiPicker: null,
        }));
    };

    // handle insert media
    onInsertMedia = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files!);
        this.setState(({ attachedMedia }) => ({
            attachedMedia: attachedMedia.concat(files),
        }));
    };

    // handle insert link
    onInsertLink = () => {
        const isValid = (url: string) => {
            let parsed;
            try {
                parsed = new URL(url);
            } catch (_) {
                return false;
            }
            return parsed.protocol === "http:" || parsed.protocol === "https:";
        };
        const { content, insertLinkUrl, insertLinkText } = this.state;
        if (isValid(insertLinkUrl)) {
            const link = `[${insertLinkText || insertLinkUrl}](${insertLinkUrl})`;
            this.setState({
                content: `${content}${link}`,
                insertLinkInvalidUrl: false,
                insertLinkText: "",
                insertLinkUrl: "",
                showLinkPopover: null,
            });
        } else {
            this.setState({ insertLinkInvalidUrl: true });
        }
    };

    // snackbar
    displayError = (error: any) => this.props.enqueueSnackbar( // eslint-disable-line
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // site popup checking
    onSitePopupCheck = () => {
        const { onClose } = this.props;
        const { clubSite } = this.state;
        onClose();
        if (clubSite) {
            this.setState({ showSiteConfirmation: true });
        } else {
            this.onCreateStory();
        }
    };

    // create story action
    onCreateStory = async () => {
        const { bookings, enqueueSnackbar, onClose } = this.props;
        const { allowComments, attachedMedia, anyOfSportForms, content, generalSportForms, postAs, postIn, isCompany, clubSite } = this.state;
        this.setState({ isLoading: true });
        try {
            await postStories({
                anyOfSportForms: anyOfSportForms.length > 0 ? anyOfSportForms : undefined,
                attachedMedia,
                authorClubId: (postAs !== "user" && postIn !== "") ? postAs : undefined,
                bookingIds: bookings?.map((b) => b.id),
                clubId: postIn || undefined,
                commentsAreEnabled: allowComments,
                content,
                authorAsCompanyProfile: (isCompany && postAs !== "user" && postIn !== "") ? true : undefined,
                clubSite: (isCompany && clubSite !== "") ? clubSite : undefined,
            });
            enqueueSnackbar(i18n.storyCreatedSuccessfully, { variant: "success" });
            this.setState({ ...initialState, generalSportForms });
            onClose();
        } catch (error) {
            this.displayError(error);
        }
        this.setState({ isLoading: false });
    };

    getAudienceSize = async (clubId?: string, anyOfSportForms?: string[], clubSite?: string) => {
        const { bookings } = this.props;
        const { isCompany } = this.state;
        try {
            const audienceSize = await postStoriesAudienceSize({
                clubId: clubId || undefined,
                anyOfSportForms: (anyOfSportForms || []).length > 0 ? anyOfSportForms : undefined,
                bookingIds: bookings?.map((b) => b.id),
                clubSite: (isCompany && clubSite !== "") ? clubSite : undefined,
            });
            this.setState(audienceSize);
        } catch (error) {
            this.displayError(error);
        }
    };

    render() {
        const { show, userProfile, onClose } = this.props;
        const { allowComments, anyOfSportForms, attachedMedia, content, generalSportForms, insertLinkInvalidUrl, insertLinkText, insertLinkUrl, isLoading, postAs, postIn, showEmojiPicker, showLinkPopover, isCompany, clubSite, audienceSize, showSiteConfirmation } = this.state;
        const contentLength = config.stories.getStrippedLength(content);
        const capitalize = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
        return (
            <>
                <Dialog
                    fullWidth
                    open={show}
                    onClose={onClose}
                >
                    <DialogTitle>{i18n.createStory}</DialogTitle>
                    <DialogContent dividers>
                        <Grid container spacing={2}>

                            {/* content */}
                            <GridTextField
                                error={contentLength > config.stories.maxLength}
                                helperText={`${contentLength} / ${config.stories.maxLength}`}
                                label={i18n.content}
                                maxLength={config.stories.absoluteMaxLength}
                                multiline
                                size={12}
                                value={content}
                                onChange={(v) => this.setState({ content: v })}
                            />

                            {/* attached media */}
                            {attachedMedia.length > 0 && (
                                <Grid item xs={12}>
                                    {attachedMedia.map((m) => (
                                        <Chip
                                            key={m.name}
                                            label={m.name}
                                            style={{ margin: "4px" }}
                                            onDelete={() => this.setState(({ attachedMedia: all }) => ({
                                                attachedMedia: all.filter((m2) => m2 !== m),
                                            }))}
                                        />
                                    ))}
                                </Grid>
                            )}
                            {/* attached media */}
                            <Grid item xs={12}>
                                <Typography color={(audienceSize === 0 ? "error" : "inherit")} align="left" variant="subtitle2">{`${i18n.audienceSize} (${audienceSize} users)`}</Typography>
                            </Grid>

                            {/* post in */}
                            <Grid item xs={6}>
                                <FormControl fullWidth>
                                    <InputLabel>{i18n.postIn}</InputLabel>
                                    <Select
                                        label={i18n.postIn}
                                        value={postIn}
                                        onChange={(e) => {
                                            const club = (userProfile?.memberships || []).filter((m) => m.club.id === e.target.value);
                                            if (club.length && club[0].club.capabilities.includes("company")) {
                                                this.setState({
                                                    postIn: e.target.value as string,
                                                    postAs: (e.target.value && postAs !== "user" ? e.target.value as string : postAs),
                                                    isCompany: true,
                                                });
                                            } else {
                                                this.setState({
                                                    postIn: e.target.value as string,
                                                    postAs: (e.target.value && postAs !== "user" ? e.target.value as string : postAs),
                                                    isCompany: undefined,
                                                });
                                            }
                                            this.getAudienceSize(e.target.value as string, anyOfSportForms, clubSite);
                                        }}
                                    >
                                        <MenuItem value=""><em>{i18n.all}</em></MenuItem>
                                        {(userProfile?.memberships || []).filter((m) => ["owner", "admin"].includes(m.userRole)).map((m) => (
                                            <MenuItem key={m.id} value={m.club.id}>{m.club.clubName}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            {/* post as */}
                            {postIn !== "" && isCompany && (
                                <Grid item xs={6}>
                                    <FormControl fullWidth>
                                        <InputLabel>{i18n.postAs}</InputLabel>
                                        <Select
                                            label={i18n.postAs}
                                            value={postAs}
                                            onChange={(e) => {
                                                this.setState({ postAs: e.target.value as string });
                                            }}
                                        >
                                            <MenuItem value="user">{`${userProfile?.firstName} ${userProfile?.lastName}`}</MenuItem>
                                            {(userProfile?.memberships || [])
                                                .filter((m) => ["owner", "admin"].includes(m.userRole))
                                                .filter((m) => m.club.id.startsWith(postIn)).map((m) => (
                                                    <MenuItem key={m.id} value={m.club.id}>{m.club.clubName}</MenuItem>
                                                ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}

                            {/* site */}
                            {isCompany && (
                                <Grid item xs={6}>
                                    <GridTextField
                                        error={Number(clubSite?.length || 0) > 50}
                                        helperText={`${Number(clubSite?.length || 0)} / ${50}`}
                                        label={i18n.site}
                                        maxLength={50}
                                        multiline
                                        size={12}
                                        value={clubSite || ""}
                                        onChange={(v) => {
                                            this.setState({ clubSite: v });
                                            this.getAudienceSize(postIn, [], v);
                                        }}
                                    />
                                </Grid>
                            )}

                            {/* audience group */}
                            <Grid item xs={6}>
                                <Autocomplete
                                    filterSelectedOptions
                                    getOptionLabel={(option) => uI18n.sportForms[option] || capitalize(option)}
                                    multiple
                                    options={generalSportForms.filter((f) => f.available).map((f) => f.name).sort()}
                                    renderInput={(params) => (
                                        <TextField {...params} label={i18n.audienceGroup} placeholder={anyOfSportForms.length === 0 ? i18n.all : ""} />
                                    )}
                                    value={anyOfSportForms}
                                    onChange={(e, v) => {
                                        this.setState({ anyOfSportForms: v });
                                        this.getAudienceSize(postIn, v, clubSite);
                                    }}
                                />
                            </Grid>

                            {/* allow comments */}
                            <Grid item xs={6} style={{ display: "flex", alignItems: "center" }}>
                                <FormControl>
                                    <FormControlLabel
                                        control={(
                                            <Checkbox
                                                checked={allowComments}
                                                color="primary"
                                                onChange={(e) => this.setState({ allowComments: e.target.checked })}
                                            />
                                        )}
                                        label={i18n.allowComments}
                                    />
                                </FormControl>
                            </Grid>

                        </Grid>
                    </DialogContent>
                    {isLoading && (
                        <LinearProgress />
                    )}
                    <DialogActions>
                        <Box style={{ marginRight: "auto" }}>
                            <Tooltip title={i18n.insertLink}>
                                <IconButton color="secondary" onClick={(e) => this.setState({ showLinkPopover: e.currentTarget })}>
                                    <LinkIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.insertMedia}>
                                <IconButton color="secondary" onClick={() => this.insertMediaRef.current!.click()}>
                                    <ImageIcon />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={i18n.insertEmoji}>
                                <IconButton color="secondary" onClick={(e) => this.setState({ showEmojiPicker: e.currentTarget })}>
                                    <EmojiIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                        <Button color="secondary" onClick={onClose}>
                            {i18n.cancel}
                        </Button>
                        {audienceSize === 0 ? (
                            <Button className="audienceSize" onClick={this.onSitePopupCheck} disabled={isLoading}>
                                {i18n.create}
                            </Button>
                        ) : (
                            <Button color="primary" onClick={this.onSitePopupCheck} disabled={isLoading}>
                                {i18n.create}
                            </Button>
                        )}

                    </DialogActions>

                    {/* insert link popover */}
                    <Popover
                        anchorEl={showLinkPopover}
                        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                        open={!!showLinkPopover}
                        onClose={() => this.setState({ showLinkPopover: null })}
                    >
                        <DialogContent>
                            <TextField
                                autoFocus
                                fullWidth
                                label={i18n.text}
                                margin="dense"
                                value={insertLinkText}
                                onChange={(e) => this.setState({ insertLinkText: e.target.value })}
                            />
                            <TextField
                                error={insertLinkInvalidUrl}
                                fullWidth
                                helperText={insertLinkInvalidUrl && i18n.notAValidUrl}
                                label={i18n.link}
                                margin="dense"
                                placeholder="https://..."
                                value={insertLinkUrl}
                                onChange={(e) => this.setState({ insertLinkUrl: e.target.value })}
                            />
                        </DialogContent>
                        <DialogActions>
                            <Button color="primary" onClick={this.onInsertLink}>
                                {i18n.ok}
                            </Button>
                        </DialogActions>
                    </Popover>

                    {/* insert media input */}
                    <input
                        accept="image/*,video/*"
                        multiple
                        ref={this.insertMediaRef}
                        style={{ display: "none" }}
                        type="file"
                        onChange={this.onInsertMedia}
                    />

                    {/* insert emoji popover */}
                    <Popover
                        anchorEl={showEmojiPicker}
                        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
                        open={!!showEmojiPicker}
                        onClose={() => this.setState({ showEmojiPicker: null })}
                    >
                        <EmojiPicker onEmojiClick={(e, { emoji }) => this.onEmojiClick(emoji)} />
                    </Popover>
                </Dialog>
                <Dialog
                    fullWidth
                    open={showSiteConfirmation}
                    onClose={() => this.setState({ showSiteConfirmation: false })}
                >
                    <DialogTitle>{i18n.alert}</DialogTitle>
                    <DialogContent dividers>
                        <DialogContent>{`${i18n.siteConfirmMessage} ${audienceSize}`}</DialogContent>
                    </DialogContent>
                    <DialogActions>
                        <Button color="primary" onClick={() => this.setState({ showSiteConfirmation: false })}>
                            {i18n.cancel}
                        </Button>
                        <Button color="primary" onClick={this.onCreateStory}>
                            {i18n.confirm}
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }
}

export default withSnackbar(CreateStoryDialog);
